/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Wed Apr 16 2025
 **/

import { BehaviorSubject } from "rxjs";
import { signInWithPopup, UserCredential } from "firebase/auth";
import { getToken, MessagePayload, onMessage } from "firebase/messaging";

import { FirebaseState } from "../../hooks/useFirebase/atom";
import { Configure, Environments } from "@/frameworks/libraries";
import { _T } from "@rfice/language";

const LOGTAG = "#FirebaseServiceController#";

type FirebaseOption = Partial<{
	onSuccess: (result?: UserCredential, idp?: "GOOGLE" | "APPLE") => void;
	onError: <E = unknown>(error?: E) => void;
	onMessage: (payload: MessagePayload) => void;
	onTokenSuccess: (token?: string) => void;
}>;

abstract class ServiceController {
	public abstract activate(): Promise<void>;
	public abstract deactivate(): Promise<void>;
}

class FirebaseServiceController extends ServiceController {
	private _clientToken$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
	/**
	 * firebase 상태
	 */
	_status$: BehaviorSubject<"idle" | "pending" | "fullfiled" | "rejected"> = new BehaviorSubject<
		"idle" | "pending" | "fullfiled" | "rejected"
	>("idle");
	private _firebaseApp$: BehaviorSubject<FirebaseState | null> = new BehaviorSubject<FirebaseState | null>(null);
	constructor() {
		super();
		this.addEvents();
	}
	public async activate() {
		try {
			this.set_messaging();
			this.checkAvailableServiceWorker();
			await this.generate_firebase_token();
		} catch (Error) {
			this._status$.next("rejected");
		}
	}

	public async deactivate(): Promise<void> {
		throw new Error("Method not implemented.");
	}

	public setFirebaseAppState(firebaseApp: FirebaseState) {
		this._firebaseApp$.next(firebaseApp);
	}

	public async handlelogin(idp: "GOOGLE" | "APPLE", options?: FirebaseOption) {
		const firebaseApp = this._firebaseApp$.getValue();
		if (!firebaseApp) {
			throw Error("Firebase state is not set");
		}

		try {
			const { auth, googleProvider, appleProvider } = firebaseApp;
			const provider = idp === "GOOGLE" ? googleProvider : appleProvider;
			const result = await signInWithPopup(auth, provider);
			options?.onSuccess?.(result, idp);
		} catch (err) {
			options?.onError?.(err);
		}
	}

	public get status$() {
		return this._status$;
	}

	public get clientToken() {
		return this._clientToken$.getValue();
	}

	public get clientToken$() {
		return this._clientToken$;
	}

	public async generate_firebase_token() {
		if (!this._firebaseApp$.getValue()) {
			throw new Error("Firebase state is not set");
		}
		// https://github.com/firebase/firebase-js-sdk/issues/7575 어떤 놈이 에러나면 걍 3~4번 재시도 하라고함;;

		let count = 4;
		while (count > 0) {
			try {
				const token = await getToken(this._firebaseApp$.getValue()!.messaging, {
					vapidKey: Environments.env<string>("FB_VAPI_KEY")
				});
				this._clientToken$.next(token);
				this._status$.next("fullfiled");
				count = 0;
			} catch {
				count--;
				this._status$.next("pending");
			}
		}
	}

	private set_messaging() {
		// eslint-disable-next-line no-async-promise-executor
		return new Promise<void>(async (resolve) => {
			//requesting permission using Notification API
			console.debug(LOGTAG, "current permission", Notification.permission);
			if (Notification.permission !== "granted") {
				if (Notification.permission === "default") {
					if (window.navigator.userAgent.includes("Edg/")) {
						if (!Configure.isNotifyOnceForNotification) {
							Configure.isNotifyOnceForNotification = true;
							throw Error(_T("알림이 차단되어 있습니다.\n메시지 수신 확인하시려면 허용해주세요."), {
								cause: {
									type: "error",
									message: _T("알림이 차단되어 있습니다.\n메시지 수신 확인하시려면 허용해주세요.")
								}
							});
						}
					} else {
						await this.run_request_permission();
					}
				} else {
					await this.run_request_permission();
				}
			} else {
				Configure.isNotifyOnceForNotification = null;
				await this.generate_firebase_token();
			}
			resolve();
		});
	}

	private run_request_permission = async () => {
		const permission = await window.Notification.requestPermission();
		if (permission === "granted") {
			await this.generate_firebase_token();
		}
	};

	private set clientToken(token: string | null) {
		this._clientToken$.next(token);
	}

	private addEvents() {
		this._firebaseApp$.subscribe((firebaseApp) => {
			if (firebaseApp) {
				//firebase 메시지 수신 이벤트 추가
				onMessage(firebaseApp.messaging, (payload: MessagePayload) => {
					console.log(LOGTAG, "FIREBASE", "onMessage", payload);
				});
			}
		});
	}

	private checkAvailableServiceWorker() {
		if ("serviceWorker" in navigator) {
			navigator.serviceWorker
				.register(
					window.location.host.startsWith("localhost")
						? "/static/firebase-messaging-sw.js"
						: "/firebase-messaging-sw.js"
				)

				.then((registration) => {
					console.log(LOGTAG, "Service worker registered", registration);
				})
				.catch((error) => {
					console.log(LOGTAG, "Service worker registration failed", error);
				});
		}
	}
}

class SingleTonFirebaseServiceController extends FirebaseServiceController {
	private static instance: SingleTonFirebaseServiceController;
	private constructor() {
		super();
	}
	public static getInstance(): SingleTonFirebaseServiceController {
		return this.instance || (this.instance = new this());
	}
}

export default SingleTonFirebaseServiceController;
